import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const steps = [
  {
    num: 1,
    label: '确定试点课程',
    desc: '明确课程和应用目标',
    delivery: '试点方案与评价框架',
    icon: 'ri-focus-2-line',
    color: 'primary' as const,
  },
  {
    num: 2,
    label: '建设指标与知识库',
    desc: '梳理目标、概念、评价点',
    delivery: '教材知识库 + 课程知识图谱',
    icon: 'ri-book-open-line',
    color: 'accent' as const,
  },
  {
    num: 3,
    label: '选取课堂样本',
    desc: '不同层次、风格、场景',
    delivery: '代表性课程样本集',
    icon: 'ri-file-list-3-line',
    color: 'primary' as const,
  },
  {
    num: 4,
    label: '专家标注与模型校准',
    desc: '人机比对，验证识别能力',
    delivery: '校准报告与验证结果',
    icon: 'ri-shield-check-line',
    color: 'accent' as const,
  },
  {
    num: 5,
    label: '形成课程模型并推广',
    desc: '验证通过后形成多端应用',
    delivery: '教师端 + 专家端 + 管理端 + 跟踪视图',
    icon: 'ri-rocket-line',
    color: 'primary' as const,
  },
];

export default function P81ThreeSentences({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.06);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.05);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-24 right-24 w-96 h-96 rounded-full bg-primary-100/10 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-20 left-16 w-[28rem] h-[28rem] rounded-full bg-accent-100/8 blur-[130px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-8 md:py-10">
        <div ref={titleRef} className="text-center mb-6 md:mb-8">
          <span className={`inline-flex items-center gap-2 text-lg font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />试点路径
          </span>
          <h2 className={`font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            从课程标准到学科模型，再到<span className="text-gold-600">规模化推广</span>
          </h2>
          <p className={`text-lg md:text-xl text-foreground-950 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            真正的试点不是把视频上传系统，而是共同建设一门课程可持续使用的评价能力
          </p>
        </div>

        {/* Five Step Path */}
        <div className={`max-w-5xl mx-auto mb-5 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {/* Desktop: horizontal flow */}
          <div className="hidden md:flex flex-wrap items-start justify-center gap-3">
            {steps.map((s, i) => (
              <div key={s.num} className="flex items-start gap-3">
                <div
                  className="p-3 glass-liquid rounded-xl text-center min-w-[180px]"
                  style={{ transitionDelay: `${300 + i * 120}ms` }}
                >
                  <div className={`w-14 h-14 mx-auto mb-2 rounded-xl ${s.color === 'accent' ? 'bg-accent-100/60 border border-accent-200/40' : 'bg-primary-100/60 border border-primary-200/40'} flex items-center justify-center`}>
                    <i className={`${s.icon} text-2xl ${s.color === 'accent' ? 'text-accent-600' : 'text-primary-600'}`} />
                  </div>
                  <span className={`text-base font-mono font-bold ${s.color === 'accent' ? 'text-accent-600' : 'text-primary-600'}`}>0{s.num}</span>
                  <h4 className="text-lg font-body font-bold text-foreground-950 mt-1">{s.label}</h4>
                  <p className="text-sm text-foreground-950 font-body mt-0.5 leading-tight">{s.desc}</p>
                  <div className="mt-2 pt-2 border-t border-background-200/50">
                    <p className="text-sm text-foreground-950 font-body italic leading-tight">{s.delivery}</p>
                  </div>
                </div>
                {i < steps.length - 1 && (
                  <div className="pt-5">
                    <i className="ri-arrow-right-line text-2xl text-foreground-950" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Mobile: vertical flow */}
          <div className="md:hidden flex flex-col items-center gap-1">
            {steps.map((s, i) => (
              <div key={s.num} className="flex flex-col items-center w-full max-w-xs">
                <div className="w-full p-4 glass-liquid rounded-xl flex items-start gap-3">
                  <div className={`w-14 h-14 rounded-xl ${s.color === 'accent' ? 'bg-gold-100/80 border border-gold-300/40' : 'bg-primary-100/60 border border-primary-200/40'} flex items-center justify-center flex-shrink-0`}>
                    <i className={`${s.icon} text-2xl ${s.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-base font-mono font-bold ${s.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`}>0{s.num}</span>
                      <h4 className="text-lg font-body font-bold text-foreground-950">{s.label}</h4>
                    </div>
                    <p className="text-base text-foreground-950 font-body">{s.desc}</p>
                    <p className="text-sm text-foreground-950 font-body italic mt-1">{s.delivery}</p>
                  </div>
                </div>
                {i < steps.length - 1 && (
                  <i className="ri-arrow-down-line text-base text-foreground-950 my-1" />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-5 glass-liquid rounded-xl">
            <p className="text-lg text-foreground-950 font-body leading-relaxed">
              试点的重点不是"系统跑起来"，而是<strong className="text-gold-700">课程标准、专家判断和模型结果能够形成可验证的一致关系</strong>。六门思政课可在天津数据基础上继续完善。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}